"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-6 items-center">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.filter(item => item.label !== "Cart" && item.label !== "Mercer2").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.label === "Northwoods Service Areas" ? "/NorthwoodsServiceAreasPage" : item.href || "#"} className="font-sans font-medium uppercase text-sm tracking-wider text-foreground/90 hover:text-primary transition-colors relative py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full">
                {item.label === "Joe's Excavating logo" ? <img src="/img/joes_excavating_logo_prominent.png" alt="Joe's Excavating" className="h-7 w-auto object-contain" /> : item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.filter(item => item.label !== "Cart" && item.label !== "Mercer2").map((item, i) => <Link key={item.id} data-index={i} to={item.label === "Northwoods Service Areas" ? "/NorthwoodsServiceAreasPage" : item.href || "#"} onClick={onNavigate} className="font-sans font-medium uppercase text-lg tracking-wider text-foreground hover:text-primary transition-colors">
          {item.label === "Joe's Excavating logo" ? <img src="/img/joes_excavating_logo_prominent.png" alt="Joe's Excavating" className="h-7 w-auto object-contain" /> : item.label}
        </Link>)}
    </div>;
}
export default function CommandCenterHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="command-center-header" className={cn("dark bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0 z-10">
          <WvcLogo className="h-14 w-auto object-contain" />
        </Link>

        <div className="hidden lg:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="57">
            <DesktopMenu />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-4 sm:gap-6 z-10">
          <Button asChild className="hidden sm:flex font-sans font-bold uppercase tracking-wide" size="lg">
            <Link to="https://joes-excavating.com/joes-excavating-lead-capture/">Get Estimate</Link>
          </Button>

          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent/20">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="dark bg-background border-l-border w-[300px] sm:w-[400px]">
              <SheetHeader className="text-left border-b border-border pb-4">
                <SheetTitle>
                  <WvcLogo className="h-6 w-auto object-contain" />
                </SheetTitle>
              </SheetHeader>
              
              <div className="py-6">
                <MenuProvider menu_id="57">
                  <MobileMenu onNavigate={() => setIsMobileMenuOpen(false)} />
                </MenuProvider>
              </div>

              <div className="mt-auto pt-6 border-t border-border">
                <Button asChild className="w-full font-sans font-bold uppercase tracking-wide" size="lg">
                  <Link to="https://joes-excavating.com/joes-excavating-lead-capture/" onClick={() => setIsMobileMenuOpen(false)}>
                    Request Materials
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}